/**
 * TanStack Query hooks for reports.
 *
 * Stale time defaults — review queue is unlikely to update faster than
 * the cron itself (monthly), so 30 s is more than enough.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { reportsApi } from '@beacon/api-client';
import type { Report, ReportDetail, ReportListItem, ReportStatus } from '@beacon/core';

import { beaconClient } from '@/lib/api/client.js';

export const reportKeys = {
  all: ['reports'] as const,
  list: (statuses?: ReadonlyArray<ReportStatus>, period?: string) =>
    [...reportKeys.all, 'list', statuses ?? 'all', period ?? 'all'] as const,
  detail: (id: string) => [...reportKeys.all, 'detail', id] as const,
};

export function useReports(statuses?: ReadonlyArray<ReportStatus>, period?: string) {
  return useQuery({
    queryKey: reportKeys.list(statuses, period),
    queryFn: async () => {
      const res = await reportsApi.list(beaconClient, {
        ...(statuses ? { statuses } : {}),
        ...(period ? { period } : {}),
      });
      return res.reports;
    },
  });
}

export function useReport(id: string | undefined) {
  return useQuery({
    queryKey: id ? reportKeys.detail(id) : reportKeys.all,
    queryFn: () => reportsApi.getById(beaconClient, id!),
    enabled: Boolean(id),
  });
}

interface ApproveContext {
  reportId: string;
  previousDetail: ReportDetail | undefined;
  previousLists: Array<{
    key: ReturnType<typeof reportKeys.list>;
    rows: ReportListItem[] | undefined;
  }>;
}

/**
 * Approve a report — fires POST /:id/approve. Optimistic patches:
 *   - the cached detail (status='approved')
 *   - any cached list pages that contained this row (so the queue
 *     row updates without a refetch when navigating back)
 *
 * Failure rolls back both caches and surfaces the error via toast.
 */
export function useApproveReport(reportId: string) {
  const qc = useQueryClient();

  return useMutation<Report, Error, void, ApproveContext>({
    mutationFn: () => reportsApi.approve(beaconClient, reportId),

    onMutate: async () => {
      const detailKey = reportKeys.detail(reportId);
      await qc.cancelQueries({ queryKey: reportKeys.all });

      const previousDetail = qc.getQueryData<ReportDetail>(detailKey);
      if (previousDetail) {
        qc.setQueryData<ReportDetail>(detailKey, {
          ...previousDetail,
          report: { ...previousDetail.report, status: 'approved' },
        });
      }

      // Walk every cached list page and patch matching rows.
      const lists = qc.getQueriesData<ReportListItem[]>({
        queryKey: [...reportKeys.all, 'list'],
      });
      const previousLists = lists.map(([key, rows]) => ({
        key: key as ReturnType<typeof reportKeys.list>,
        rows,
      }));
      for (const [key, rows] of lists) {
        if (!rows) continue;
        qc.setQueryData<ReportListItem[]>(
          key,
          rows.map((r) =>
            r.id === reportId ? { ...r, status: 'approved' } : r
          )
        );
      }

      return { reportId, previousDetail, previousLists };
    },

    onSuccess: (server) => {
      const detailKey = reportKeys.detail(server.id);
      qc.setQueryData<ReportDetail>(detailKey, (current) =>
        current ? { ...current, report: server } : current
      );
      // List rows may have moved tab (review → approved) — invalidate
      // so the next visit refetches the right tab's contents.
      qc.invalidateQueries({ queryKey: [...reportKeys.all, 'list'] });
    },

    onError: (err, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.previousDetail !== undefined) {
        qc.setQueryData(reportKeys.detail(ctx.reportId), ctx.previousDetail);
      }
      for (const { key, rows } of ctx.previousLists) {
        if (rows !== undefined) qc.setQueryData(key, rows);
      }
      toast.error("Couldn't approve report", { description: err.message });
    },
  });
}

interface SendContext {
  reportId: string;
  previousDetail: ReportDetail | undefined;
  previousLists: Array<{
    key: ReturnType<typeof reportKeys.list>;
    rows: ReportListItem[] | undefined;
  }>;
}

/**
 * Send an approved report to a recipient list. Optimistic patches:
 *   - cached detail flips to 'sent' with the sent recipients
 *   - any cached list rows flip to 'sent'
 *
 * Errors roll the caches back. The send itself is fire-and-forget on
 * the server (rows go into the outbound queue), so success here means
 * "queued", not "delivered" — the toast wording reflects that.
 */
export function useSendReport(reportId: string) {
  const qc = useQueryClient();

  return useMutation<Report, Error, ReadonlyArray<string>, SendContext>({
    mutationFn: (recipients) =>
      reportsApi.send(beaconClient, reportId, recipients),

    onMutate: async (recipients) => {
      const detailKey = reportKeys.detail(reportId);
      await qc.cancelQueries({ queryKey: reportKeys.all });

      const previousDetail = qc.getQueryData<ReportDetail>(detailKey);
      if (previousDetail) {
        qc.setQueryData<ReportDetail>(detailKey, {
          ...previousDetail,
          report: {
            ...previousDetail.report,
            status: 'sent',
            sentToEmails: [...recipients],
          },
        });
      }

      const lists = qc.getQueriesData<ReportListItem[]>({
        queryKey: [...reportKeys.all, 'list'],
      });
      const previousLists = lists.map(([key, rows]) => ({
        key: key as ReturnType<typeof reportKeys.list>,
        rows,
      }));
      for (const [key, rows] of lists) {
        if (!rows) continue;
        qc.setQueryData<ReportListItem[]>(
          key,
          rows.map((r) =>
            r.id === reportId
              ? { ...r, status: 'sent', sentToEmails: [...recipients] }
              : r
          )
        );
      }

      return { reportId, previousDetail, previousLists };
    },

    onSuccess: (server) => {
      const detailKey = reportKeys.detail(server.id);
      qc.setQueryData<ReportDetail>(detailKey, (current) =>
        current ? { ...current, report: server } : current
      );
      qc.invalidateQueries({ queryKey: [...reportKeys.all, 'list'] });
    },

    onError: (err, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.previousDetail !== undefined) {
        qc.setQueryData(reportKeys.detail(ctx.reportId), ctx.previousDetail);
      }
      for (const { key, rows } of ctx.previousLists) {
        if (rows !== undefined) qc.setQueryData(key, rows);
      }
      toast.error("Couldn't send report", { description: err.message });
    },
  });
}

/**
 * Save cover notes on a draft/review report. On success, invalidates
 * the detail query so the live preview re-renders with the new notes.
 */
export function useUpdateCoverNotes(reportId: string) {
  const qc = useQueryClient();

  return useMutation<Report, Error, string>({
    mutationFn: (coverNotes) =>
      reportsApi.updateCoverNotes(beaconClient, reportId, coverNotes),

    onSuccess: (server) => {
      const detailKey = reportKeys.detail(server.id);
      // Invalidate the detail so the HTML preview re-renders with the
      // new cover notes on the next fetch.
      qc.invalidateQueries({ queryKey: detailKey });
    },

    onError: (err) => {
      toast.error("Couldn't save cover notes", { description: err.message });
    },
  });
}

interface RejectInput {
  reason:
    | 'hours_mismatch'
    | 'thin_notes'
    | 'attribution'
    | 'duplicate'
    | 'commercial'
    | 'other';
  note: string;
}

/**
 * Reject (request changes on) a report. Flips the cached detail's
 * status to 'draft' optimistically and invalidates list queries on
 * success so the row moves out of the Review tab and into Draft.
 */
export function useRejectReport(reportId: string) {
  const qc = useQueryClient();

  return useMutation<Report, Error, RejectInput>({
    mutationFn: (input) => reportsApi.reject(beaconClient, reportId, input),

    onSuccess: (server) => {
      const detailKey = reportKeys.detail(server.id);
      qc.setQueryData<ReportDetail>(detailKey, (current) =>
        current ? { ...current, report: server } : current
      );
      // Row moves Review → Draft — invalidate so both tabs refetch.
      qc.invalidateQueries({ queryKey: [...reportKeys.all, 'list'] });
    },

    onError: (err) => {
      toast.error("Couldn't reject report", { description: err.message });
    },
  });
}
